import {Capacitor} from "@capacitor/core";
import {Card, CardBody, Checkbox, SelectItem} from "@heroui/react";
import {addToast} from "@heroui/toast";
import {Icon} from "@iconify/react";
import {useMutation} from "@tanstack/react-query";
import {Link, useNavigate, useSearch} from "@tanstack/react-router";
import React, {useState} from "react";
import PButton from "../components/PButton.tsx";
import PInput from "../components/PInput.tsx";
import PSelect from "../components/PSelect.tsx";
import PSelectCountry from "../components/PSelectCountry.tsx";
import PAutocompleteCity from "../components/PAutocompleteCity.tsx";
import {ClubType, GradeToText, SportTypeIcons, UserRole} from "../constants.ts";
import {sportDefinition, sportsForSurface, useSports} from "../sports/registry.ts";
import {SPORT_AREAS, sportAreaHome} from "../sports/sportAreas.ts";
import {useGlobalModal} from "../core/GlobalModal.tsx";
import {config} from "@/config/config.ts";
import Header from "../Header.tsx";
import {useJwtAuth} from "../JWTAuthProvider.tsx";
import {Player} from "../types";
import LegalModal from "../user/LegalModal.tsx";
import {apiAction} from './../api';

const STEP_ROLE = 1;
const STEP_SPORT = 2;
const STEP_PLAYER_DETAILS = 3;
const STEP_ORGANIZER_TYPE = 3;
const STEP_CLUB_BOOKING = 4;
const STEP_ORGANIZER_DETAILS = 5;
const STEP_SIGNUP_FORM = 6;

// The signup sport cards, from the sport registry's 'signup' surface (icons from the definitions).
// Read at call time, never at module load (see quiz.tsx).
export const signupSports = () => sportsForSurface('signup').map((entry) => ({
    key:   entry.key,
    label: entry.label,
    icon:  sportDefinition(entry.key)?.icon || SportTypeIcons[entry.key] || 'material-symbols-light:sports',
}));

export function SignupComponent({join_tournament_uid = null as any, join_league_uid = null as any, onComplete, playerOnly = false, initialRole, routeMode = false, presetSport, presetEmail}: {join_tournament_uid?: any; join_league_uid?: any; onComplete?: () => void; playerOnly?: boolean; initialRole?: 'player' | 'organizer'; routeMode?: boolean;
    /**
     * Signing up into something that already knows the answers — a club manager accepting an
     * invite. The sport comes from the club they're joining and the address from the invite, so
     * the role, sport and player-detail steps are all skipped and they land on the form itself.
     */
    presetSport?: string | null; presetEmail?: string | null} = {}) {
    const navigate = useNavigate();
    const jwt = useJwtAuth();
    const search = useSearch<any>({strict: false} as any);
    const globalModal = useGlobalModal();

    // Only the organizer URL (/signup/organizer) skips the role picker. The default entry
    // (/signup and /signup/player) still shows the Player/Organizer choice, and event
    // registration (playerOnly) fixes the role to Player. When the role is fixed up front the
    // role-picker step is skipped and the wizard opens straight on the sport step.
    const roleFromUrl = initialRole === 'organizer' ? UserRole.Organizer : null;
    const hasRoleStep = !playerOnly && roleFromUrl === null;
    const redirectSearch = (search as {redirect?: string}).redirect ? {redirect: (search as {redirect?: string}).redirect} : {};

    const [step, setStep] = useState(presetSport ? STEP_SIGNUP_FORM : (hasRoleStep ? STEP_ROLE : STEP_SPORT));
    const [role, setRole] = useState<number>(roleFromUrl ?? UserRole.Player);
    const [sport, setSport] = useState(presetSport || '');

    // Player details
    const [howLongPlaying, setHowLongPlaying] = useState('');
    const [eloGrade, setEloGrade] = useState('C+');
    const [howOften, setHowOften] = useState('weekly');
    const [nationality, setNationality] = useState('');

    // Organizer details
    const [clubType, setClubType] = useState<number>(ClubType.Venue);
    const [clubName, setClubName] = useState('');
    const [clubCountryCode, setClubCountryCode] = useState('');
    const [clubCity, setClubCity] = useState('');
    const [clubCityCoordinates, setClubCityCoordinates] = useState<any>(null);
    const [wantBookingModule, setWantBookingModule] = useState('');
    const [bookingSystemDetails, setBookingSystemDetails] = useState('');

    // Signup form
    const [accepted, setAccepted] = useState(false);
    const [showAcceptError, setShowAcceptError] = useState(false);
    const [joinLeaderboard, setJoinLeaderboard] = useState(true);
    const [isVisible, setIsVisible] = useState(false);
    const [showPasswordError, setShowPasswordError] = useState(false);
    const [profile, setProfile] = useState<Partial<Player> & { password: string }>({
        email: presetEmail || search.em || '',
        password: '',
        name: '',
        lastname: '',
        inviter_uid: search.invitedBy || (typeof localStorage !== 'undefined' && localStorage.inviter_uid) || '',
    });

    /**
     * Mirrors the server policy in config/security.php. Kept as a list rather than a single
     * boolean so the form can show WHICH requirement is outstanding while the user types — being
     * told "invalid password" without being told why is what makes people abandon a signup.
     *
     * The breach check is deliberately absent: it needs the HaveIBeenPwned corpus and lives on the
     * server. If it rejects, the server's message surfaces through the existing error handling.
     */
    const PASSWORD_RULES: {label: string; ok: (p: string) => boolean}[] = [
        {label: 'At least 8 characters', ok: (p) => p.length >= 8},
        {label: 'A letter', ok: (p) => /\p{L}/u.test(p)},
        {label: 'A number', ok: (p) => /\d/.test(p)},
    ];
    const unmetPasswordRules = PASSWORD_RULES.filter((r) => !r.ok(profile.password || ''));

    const doSignup = (event: any) => {
        event.preventDefault();

        // Password policy, checked here so the answer is immediate and specific rather than a
        // round trip ending in an alert(). The SERVER is the authority (ProfileController
        // ::assertPasswordAcceptable) — this only saves the user a wasted submit, and cannot be
        // the whole check because the server also tests the password against the breach corpus,
        // which is not something the browser can or should do.
        if (unmetPasswordRules.length > 0) {
            setShowPasswordError(true);
            return;
        }

        // The T&C box is checked here rather than left to the browser's `required`. HeroUI renders
        // the native checkbox at opacity 0.0001, so a validation bubble anchored to it is easy to
        // miss — the page just looked dead, and Sign Up was showing up in the rage-click log. An
        // in-page message can't be missed the way that bubble can.
        if (!accepted) {
            setShowAcceptError(true);
            return;
        }
        doSignupMut.mutate();
    }

    const doSignupMut = useMutation({
        mutationFn: () => {
            return apiAction('signup', {
                type: (role === UserRole.Player ? 'player' : 'organizer'),
                landing_url: jwt.landingUrl,
                platform: Capacitor.getPlatform(),
                join_tournament_uid,
                join_league_uid,
                profile: {...profile, leaderboard_enrolled: leaderboardOffered && joinLeaderboard},
                questionnaire: {
                    sport,
                    elo_grade: role === UserRole.Player ? eloGrade : null,
                    how_long_playing: role === UserRole.Player ? howLongPlaying : null,
                    how_often: role === UserRole.Player ? howOften : null,
                    nationality: role === UserRole.Player ? nationality : null,
                    club: role === UserRole.Organizer ? {
                        type: clubType,
                        name: clubName,
                        country_code: clubCountryCode,
                        city: clubCity,
                        city_coordinates: clubCityCoordinates,
                        meta: {
                            uses_booking_system: wantBookingModule,
                            booking_system_details: bookingSystemDetails || undefined,
                        },
                    } : null,
                },
            })
        },
        onSettled: (res) => {
            if (res.error) {
                alert(res.error);
                return;
            }

            if (res.showMessage) {
                addToast({
                    title: res.showMessage,
                    color: "success",
                })
            }

            jwt.setFieldsFromResponse(res);
            if (onComplete) {
                onComplete();
                return;
            }
            // Work out where the new account should land, then route through a distinctive
            // /signup/thank-you/ URL so Google Ads / analytics can track the signup conversion
            // before the thank-you page forwards on to this destination.
            const signupArea = SPORT_AREAS[sport];
            const nextPath = signupArea
                ? sportAreaHome(signupArea)
                : ((search as { redirect?: string }).redirect || res.redirectTo || '/profiles/me/');
            const signupType = role === UserRole.Player ? 'player' : 'organizer';
            navigate({to: '/signup/thank-you/', search: {next: nextPath, type: signupType}} as any);
        },
    })

    function openLink(e: any, url: string) {
        e.stopPropagation();
        globalModal.open(<LegalModal pathname={url}/>)
    }

    // Team sport areas (football/basketball/esports) skip the Club-vs-Community step;
    // racquet sports keep it (and the Club path adds the booking-module step).
    const isAreaSport = !!SPORT_AREAS[sport];
    // League of Leagues ranks team-sport leagues too, so their organizers get the same pre-ticked
    // box as racquet organizers. Not team-sport players (no personal ladder in those sports) and
    // not race areas (no leagues to rank).
    const leaderboardOffered = !isAreaSport || (role !== UserRole.Player && !SPORT_AREAS[sport].isRaceArea);

    const getStepNumber = () => {
        const offset = hasRoleStep ? 0 : 1; // the role-picker step is hidden when the URL fixes the role
        if (role === UserRole.Player) {
            if (step === STEP_ROLE) return 1;
            if (step === STEP_SPORT) return 2 - offset;
            if (step === STEP_PLAYER_DETAILS) return 3 - offset;
            if (step >= STEP_SIGNUP_FORM) return 4 - offset;
        } else if (isAreaSport) {
            if (step === STEP_ROLE) return 1;
            if (step === STEP_SPORT) return 2 - offset;
            if (step === STEP_ORGANIZER_DETAILS) return 3 - offset;
            if (step >= STEP_SIGNUP_FORM) return 4 - offset;
        } else {
            if (step === STEP_ROLE) return 1;
            if (step === STEP_SPORT) return 2 - offset;
            if (step === STEP_ORGANIZER_TYPE) return 3 - offset;
            if (clubType === ClubType.Venue) {
                if (step === STEP_CLUB_BOOKING) return 4 - offset;
                if (step === STEP_ORGANIZER_DETAILS) return 5 - offset;
                if (step >= STEP_SIGNUP_FORM) return 6 - offset;
            } else {
                if (step === STEP_ORGANIZER_DETAILS) return 4 - offset;
                if (step >= STEP_SIGNUP_FORM) return 5 - offset;
            }
        }
        return step;
    };
    const totalSteps = (role === UserRole.Player || isAreaSport ? 4 : (clubType === ClubType.Venue ? 6 : 5)) - (hasRoleStep ? 0 : 1);
    const progress = Math.round((getStepNumber() / totalSteps) * 100);

    const canGoBack = step > (hasRoleStep ? STEP_ROLE : STEP_SPORT);

    const goBack = () => {
        if (step === STEP_SIGNUP_FORM) {
            setStep(role === UserRole.Player ? STEP_PLAYER_DETAILS : STEP_ORGANIZER_DETAILS);
        } else if (step === STEP_ORGANIZER_DETAILS) {
            if (role === UserRole.Organizer && !isAreaSport) {
                setStep(clubType === ClubType.Venue ? STEP_CLUB_BOOKING : STEP_ORGANIZER_TYPE);
            } else {
                setStep(STEP_SPORT);
            }
        } else if (step === STEP_CLUB_BOOKING) {
            setStep(STEP_ORGANIZER_TYPE);
        } else {
            setStep(step - 1);
        }
    };

    return <>
        <title>ScorePal Signup</title>

        {step > STEP_ROLE && <div className="mb-4">
            <div className="flex justify-between text-xs text-default-500 mb-1">
                <span>Step {getStepNumber()} of {totalSteps}</span>
                <span>{progress}%</span>
            </div>
            <div className="w-full bg-default-200 rounded-full h-2">
                <div className="bg-primary h-2 rounded-full transition-all duration-300" style={{width: `${progress}%`}}></div>
            </div>
        </div>}

        {canGoBack && <div className="mb-3">
            <PButton variant="light" size="sm" onPress={goBack}
                     startContent={<Icon icon="material-symbols-light:arrow-back" className="text-lg"/>}>
                Back
            </PButton>
        </div>}

        {/* Step 1: Choose Role */}
        {step === STEP_ROLE && <Card>
            <CardBody className="text-center p-6">
                <h2 className="text-xl font-bold mb-2">Welcome to ScorePal</h2>
                <p className="text-default-500 mb-6">Are you a Player or an Organizer?</p>

                <div className="grid grid-cols-2 gap-4">
                    <Card isPressable onPress={() => {
                        setRole(UserRole.Player);
                        setStep(STEP_SPORT);
                    }}
                          className="cursor-pointer hover:bg-default-100 transition-all">
                        <CardBody className="flex flex-col items-center justify-center p-6 gap-3">
                            <Icon icon="material-symbols-light:person" className="text-5xl text-primary"/>
                            <span className="font-semibold text-lg">Player</span>
                            <span className="text-xs text-default-500">Join matches and track your stats</span>
                        </CardBody>
                    </Card>

                    <Card isPressable onPress={() => {
                        if (routeMode) {
                            navigate({to: "/signup/organizer/" as any, search: redirectSearch as any});
                        } else {
                            setRole(UserRole.Organizer);
                            setStep(STEP_SPORT);
                        }
                    }}
                          className="cursor-pointer hover:bg-default-100 transition-all">
                        <CardBody className="flex flex-col items-center justify-center p-6 gap-3">
                            <Icon icon="material-symbols-light:groups" className="text-5xl text-primary"/>
                            <span className="font-semibold text-lg">Organizer</span>
                            <span className="text-xs text-default-500">Create leagues and tournaments</span>
                        </CardBody>
                    </Card>
                </div>

                <div className="mt-6 text-center text-sm text-default-500">
                    Already have an account?{' '}
                    <Link to="/login/" search={(search as {redirect?: string}).redirect ? {redirect: (search as {redirect?: string}).redirect} : {}} className="underline text-primary">Login</Link>
                </div>
            </CardBody>
        </Card>}

        {/* Step 2: Choose Sport */}
        {step === STEP_SPORT && <Card>
            <CardBody className="text-center p-6">
                <h2 className="text-xl font-bold mb-2">{role === UserRole.Organizer ? 'What sport do you manage?' : 'What sport do you play?'}</h2>
                <p className="text-default-500 mb-6">Select your primary sport</p>

                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {signupSports()
                        // Running is dark on production until the races area is ready — see
                        // config.featureEnableRunning. Gate it here rather than in signupSports so
                        // the list stays a plain read of the registry.
                        .filter((s) => s.key !== 'running' || config.featureEnableRunning)
                        .filter((s) => role === UserRole.Organizer || !SPORT_AREAS[s.key]).map((s) => (
                        <Card key={s.key} isPressable onPress={() => {
                            setSport(s.key);
                            if (s.key === 'padel' && eloGrade === '') setEloGrade('C+');
                            if (s.key !== 'padel') setEloGrade('C+');
                            if (role === UserRole.Organizer) {
                                if (SPORT_AREAS[s.key]) {
                                    // Team sport areas (football, basketball, esports) don't differentiate
                                    // Club vs Community — default to Community and skip to details.
                                    setClubType(ClubType.Community);
                                    setStep(STEP_ORGANIZER_DETAILS);
                                } else {
                                    // Racquet sports (padel, tennis, badminton, squash, pickleball):
                                    // organizers choose Club or Community on the next screen.
                                    setStep(STEP_ORGANIZER_TYPE);
                                }
                            } else {
                                setStep(STEP_PLAYER_DETAILS);
                            }
                        }}
                              className={`cursor-pointer transition-all ${sport === s.key ? 'ring-2 ring-primary bg-primary-50' : 'hover:bg-default-100'}`}>
                            <CardBody className="flex flex-col items-center justify-center p-4 gap-2">
                                <Icon icon={s.icon} className="text-3xl"/>
                                <span className="text-sm font-medium">{s.label}</span>
                            </CardBody>
                        </Card>
                    ))}
                </div>
            </CardBody>
        </Card>}

        {/* Step 3 (Player): Player Details */}
        {step === STEP_PLAYER_DETAILS && role === UserRole.Player && <Card>
            <CardBody className="p-6">
                <h2 className="text-xl font-bold mb-4 text-center">Tell us about your game</h2>

                <form className="flex flex-col gap-6" onSubmit={(e) => {
                    e.preventDefault();
                    setStep(STEP_SIGNUP_FORM);
                }}>
                    <PSelect
                        selectedKeys={[howLongPlaying || '']}
                        isRequired={true}
                        label="How long have you been playing?"
                        classNames={{label: "w-full", helperWrapper: "hidden!"}}
                        onChange={(e) => setHowLongPlaying(e.target.value)}
                    >
                        <SelectItem key="less than 1 year">Less than 1 year</SelectItem>
                        <SelectItem key="1 year">1 year</SelectItem>
                        <SelectItem key="2 years">2 years</SelectItem>
                        <SelectItem key="3+ years">3+ years</SelectItem>
                    </PSelect>

                    <PSelect
                        label="What Player Grade are you?"
                        isRequired={true}
                        defaultSelectedKeys={[eloGrade]}
                        classNames={{label: "w-full", helperWrapper: "hidden!"}}
                        onChange={(e) => setEloGrade(e.target.value)}
                    >
                        {['A', 'B+', 'B', 'B-', 'C+', 'C', 'C-', 'D', 'Beginner'].map(
                            (grade) => <SelectItem key={grade}
                                                   aria-label={GradeToText(grade)}>{GradeToText(grade, sport)}</SelectItem>
                        )}
                    </PSelect>

                    <PSelect
                        label="How often do you play?"
                        isRequired={true}
                        defaultSelectedKeys={[howOften]}
                        classNames={{label: "w-full", helperWrapper: "hidden!"}}
                        onChange={(e) => setHowOften(e.target.value)}
                    >
                        <SelectItem key="rarely">Very rarely</SelectItem>
                        <SelectItem key="monthly">Once a month</SelectItem>
                        <SelectItem key="bimonthly">Two times a month</SelectItem>
                        <SelectItem key="weekly">Every week</SelectItem>
                        <SelectItem key="biweekly">Twice per week</SelectItem>
                        <SelectItem key="often">Three times per week or more</SelectItem>
                    </PSelect>

                    <PSelectCountry
                        isRequired={false}
                        selectedKeys={[nationality || '']}
                        classNames={{helperWrapper: "hidden!"}}
                        onValueChange={(code) => setNationality(code)}
                        label="What Nationality are you? (optional)"
                    ></PSelectCountry>

                    <div className="text-center mt-2">
                        <PButton color="primary" type="submit"
                                 endContent={<Icon icon="material-symbols-light:arrow-forward" className="text-lg"/>}>
                            Continue
                        </PButton>
                    </div>
                </form>
            </CardBody>
        </Card>}

        {/* Step 3 (Organizer): Club or Community */}
        {step === STEP_ORGANIZER_TYPE && role === UserRole.Organizer && <Card>
            <CardBody className="text-center p-6">
                <h2 className="text-xl font-bold mb-2">Are you a Club or Community?</h2>
                <p className="text-default-500 mb-6">This helps us set up the right features for you</p>

                <div className="grid grid-cols-2 gap-4">
                    <Card isPressable onPress={() => {
                        setClubType(ClubType.Venue);
                        setStep(STEP_CLUB_BOOKING);
                    }}
                          className={`cursor-pointer transition-all ${clubType === ClubType.Venue ? 'ring-2 ring-primary bg-primary-50' : 'hover:bg-default-100'}`}>
                        <CardBody className="flex flex-col items-center justify-center p-6 gap-3">
                            <Icon icon="material-symbols-light:location-on" className="text-4xl text-primary"/>
                            <span className="font-semibold">Club</span>
                            <span className="text-xs text-default-500">Physical Venue</span>
                        </CardBody>
                    </Card>

                    <Card isPressable onPress={() => {
                        setClubType(ClubType.Community);
                        setStep(STEP_ORGANIZER_DETAILS);
                    }}
                          className={`cursor-pointer transition-all ${clubType === ClubType.Community ? 'ring-2 ring-primary bg-primary-50' : 'hover:bg-default-100'}`}>
                        <CardBody className="flex flex-col items-center justify-center p-6 gap-3">
                            <Icon icon="material-symbols-light:groups-2" className="text-4xl text-primary"/>
                            <span className="font-semibold">Community</span>
                            <span className="text-xs text-default-500">Group of players</span>
                        </CardBody>
                    </Card>
                </div>
            </CardBody>
        </Card>}

        {/* Step 4 (Club only): Court Booking Module */}
        {step === STEP_CLUB_BOOKING && role === UserRole.Organizer && clubType === ClubType.Venue && <Card>
            <CardBody className="text-center p-6">
                <h2 className="text-xl font-bold mb-2">Court Booking Module</h2>
                <p className="text-default-500 mb-6">Do you want the court booking module enabled?</p>

                <div className="grid grid-cols-2 gap-4 max-w-xs mx-auto">
                    <Card isPressable onPress={() => setWantBookingModule('yes')}
                          className={`cursor-pointer transition-all ${wantBookingModule === 'yes' ? 'ring-2 ring-primary bg-primary-50' : 'hover:bg-default-100'}`}>
                        <CardBody className="flex flex-col items-center justify-center p-6 gap-2">
                            <Icon icon="material-symbols-light:check-circle" className="text-4xl text-success"/>
                            <span className="font-semibold">Yes</span>
                        </CardBody>
                    </Card>

                    <Card isPressable onPress={() => {
                        setWantBookingModule('no');
                        setBookingSystemDetails('');
                        setStep(STEP_ORGANIZER_DETAILS);
                    }}
                          className={`cursor-pointer transition-all ${wantBookingModule === 'no' ? 'ring-2 ring-primary bg-primary-50' : 'hover:bg-default-100'}`}>
                        <CardBody className="flex flex-col items-center justify-center p-6 gap-2">
                            <Icon icon="material-symbols-light:cancel-outline" className="text-4xl text-default-400"/>
                            <span className="font-semibold">No</span>
                        </CardBody>
                    </Card>
                </div>

                {wantBookingModule === 'yes' && <form className="flex flex-col gap-6 mt-6 text-left" onSubmit={(e) => {
                    e.preventDefault();
                    setStep(STEP_ORGANIZER_DETAILS);
                }}>
                    <PInput
                        label="Which booking system do you currently use?"
                        placeholder="e.g. Matchi, Playtomic, custom system..."
                        value={bookingSystemDetails}
                        onChange={(e) => setBookingSystemDetails(e.target.value)}
                    />

                    <div className="text-center">
                        <PButton color="primary" type="submit"
                                 endContent={<Icon icon="material-symbols-light:arrow-forward" className="text-lg"/>}>
                            Continue
                        </PButton>
                    </div>
                </form>}
            </CardBody>
        </Card>}

        {/* Step 5 (Organizer): Club/Community Details */}
        {step === STEP_ORGANIZER_DETAILS && role === UserRole.Organizer && <Card>
            <CardBody className="p-6">
                <h2 className="text-xl font-bold mb-4 text-center">
                    {isAreaSport ? 'Organizer Details' : clubType === ClubType.Venue ? 'Club Details' : 'Community Details'}
                </h2>

                <form className="flex flex-col gap-6" onSubmit={(e) => {
                    e.preventDefault();
                    setStep(STEP_SIGNUP_FORM);
                }}>
                    <PInput
                        label={isAreaSport ? "Organization Name" : clubType === ClubType.Venue ? "Club Name" : "Community Name"}
                        isRequired={true}
                        value={clubName}
                        onChange={(e) => setClubName(e.target.value)}
                    />

                    <PSelectCountry
                        isRequired={true}
                        selectedKeys={[clubCountryCode || '']}
                        classNames={{helperWrapper: "hidden!"}}
                        onValueChange={(code) => {
                            setClubCountryCode(code);
                            setClubCity('');
                            setClubCityCoordinates(null);
                        }}
                        label="Country"
                    ></PSelectCountry>

                    <PAutocompleteCity
                        label="City"
                        countryCode={clubCountryCode}
                        selectedKey={clubCity || ''}
                        onSelectionChange={(place: any) => {
                            if (place) {
                                setClubCity(place.place || '');
                                setClubCityCoordinates({type: "Point", coordinates: [place.longitude, place.latitude]});
                            } else {
                                setClubCity('');
                                setClubCityCoordinates(null);
                            }
                        }}
                    />

                    <div className="text-center mt-2">
                        <PButton color="primary" type="submit"
                                 endContent={<Icon icon="material-symbols-light:arrow-forward" className="text-lg"/>}>
                            Continue
                        </PButton>
                    </div>
                </form>
            </CardBody>
        </Card>}

        {/* Final Step: Signup Form */}
        {step === STEP_SIGNUP_FORM && <Card>
            <CardBody className="p-6">
                <h2 className="text-xl font-bold mb-4 text-center">Create your account</h2>

                <form onSubmit={doSignup} className="*:mt-3">
                    <PInput label="Firstname" placeholder="" type="text"
                            name={"firstname"}
                            autoComplete={"given-name"}
                            required={true}
                            value={profile.name}
                            onChange={(e) => {
                                profile.name = e.target.value;
                                setProfile({...profile});
                            }}
                    />

                    <PInput label="Lastname" placeholder="" type="text"
                            name={"lastname"}
                            autoComplete={"family-name"}
                            required={true}
                            value={profile.lastname}
                            onChange={(e) => {
                                profile.lastname = e.target.value;
                                setProfile({...profile});
                            }}
                    />

                    <PInput label="Email" placeholder="Enter your email" type="email"
                            name={"email"}
                            autoComplete={"email"}
                            required={true}
                            value={profile.email}
                            // An invited manager must sign up with the address the invite was sent
                            // to — accept checks it — so don't invite them to change it here.
                            isReadOnly={!!presetEmail}
                            description={presetEmail ? 'The address your invitation was sent to.' : undefined}
                            onChange={(e) => {
                                profile.email = e.target.value;
                                setProfile({...profile});
                            }}
                    />

                    <PInput label="Password" placeholder="Enter your password"
                            type={isVisible ? 'text' : 'password'}
                            name={"password"}
                            autoComplete={"new-password"}
                            endContent={
                                <button
                                    aria-label="toggle password visibility"
                                    className="focus:outline-none"
                                    type="button"
                                    onClick={() => setIsVisible(!isVisible)}
                                >
                                    {isVisible ? (
                                        <Icon icon={"mdi-light:eye-off"} className={"text-xl"}/>
                                    ) : (
                                        <Icon icon={"mdi-light:eye"} className={"text-xl"}/>
                                    )}
                                </button>
                            }
                            required={true}
                            value={profile.password}
                            isInvalid={showPasswordError && unmetPasswordRules.length > 0}
                            onChange={(e) => {
                                profile.password = e.target.value;
                                setProfile({...profile});
                                if (showPasswordError) setShowPasswordError(false);
                            }}
                    />

                    {/* Requirements, shown as a live checklist.
                        Only once they have started typing: an untouched form that already looks
                        like it is telling you off is worse than no guidance at all. After a blocked
                        submit it stays visible regardless, so the reason for the block is on screen. */}
                    {((profile.password || '').length > 0 || showPasswordError) && (
                        <ul className="mt-2 space-y-1 text-xs" aria-live="polite">
                            {PASSWORD_RULES.map((rule) => {
                                const ok = rule.ok(profile.password || '');
                                return (
                                    <li key={rule.label}
                                        className={`flex items-center gap-1.5 ${ok ? 'text-success-600' : 'text-default-500'}`}>
                                        <Icon
                                            icon={ok ? 'material-symbols-light:check-circle-outline' : 'material-symbols-light:circle-outline'}
                                            className="text-base shrink-0"
                                        />
                                        {rule.label}
                                    </li>
                                );
                            })}
                        </ul>
                    )}

                    {/* Deliberately NOT isRequired: the native constraint blocked submit before
                        doSignup ever ran, silently, against an all-but-invisible input. doSignup
                        checks `accepted` itself and shows the message below. */}
                    <div className="flex items-center mt-4!">
                        <Checkbox isSelected={accepted}
                                  onValueChange={(v) => { setAccepted(v); if (v) setShowAcceptError(false); }}
                                  className=""
                        ></Checkbox>

                        <div className="cursor-pointer" onClick={() => { setAccepted(!accepted); if (!accepted) setShowAcceptError(false); }}>
                            Agree to{' '}
                            <a onClick={(e: any) => openLink(e, '/terms/')} className="underline">T&C's</a>
                            {' '}and{' '}
                            <a onClick={(e: any) => openLink(e, '/privacy/')} className="underline">Privacy
                                Policy</a>
                        </div>
                    </div>
                    {showAcceptError && (
                        <p role="alert" className="text-danger text-sm mt-1!">
                            Please accept the T&amp;C's and Privacy Policy to continue.
                        </p>
                    )}

                    {leaderboardOffered && (
                    <div className="flex items-center mt-3!">
                        <Checkbox isSelected={joinLeaderboard}
                                  onValueChange={setJoinLeaderboard}
                        ></Checkbox>

                        <div className="cursor-pointer text-sm" onClick={() => setJoinLeaderboard(!joinLeaderboard)}>
                            Join the ScorePal Leaderboards (optional)
                            <span className="whitespace-nowrap">
                                {' '}—{' '}
                                <a onClick={(e: any) => openLink(e, '/leaderboard-terms/')} className="underline">terms</a>
                            </span>
                        </div>
                    </div>
                    )}

                    <div className="text-center mt-6!">
                        <PButton type="submit" color="primary" isLoading={doSignupMut.isPending}>Sign Up</PButton>
                    </div>
                </form>
            </CardBody>
        </Card>}
    </>
}

const SignupView = ({initialRole}: {initialRole?: 'player' | 'organizer'} = {}) => {
    useSports(); // re-render with the full sport list once the live registry lands
    return (
        <>
            <title>Tournament & League Management Software | Sports Tournament Software - ScorePal</title>
            <meta name="robots" content="noindex, nofollow"/>

            <Header backUrl={"/login/"} isPublicPage={true}/>

            <div className="max-w-md mx-auto flex w-full flex-col px-4">
                <SignupComponent initialRole={initialRole} routeMode/>
            </div>
        </>
    );
};

export default SignupView;
